import { useRef, useEffect, useState } from 'react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import {
  ArrowRight,
  Gift,
  Zap,
  Shield,
  Wifi,
  Users,
  Pill,
  Shirt,
  Utensils,
  Clapperboard,
  Plane,
  ShoppingBag,
  Star,
  Quote,
  CheckCircle,
  Youtube,
} from 'lucide-react';

const WHATSAPP_CLUB =
  'https://wa.me/5551996777625?text=Ol%C3%A1!%20Gostaria%20de%20conhecer%20mais%20sobre%20o%20iGreen%20Club%20e%20os%20benef%C3%ADcios%20dispon%C3%ADveis%20na%20minha%20regi%C3%A3o.';

const CLUB_PLAYLIST_URL =
  'https://www.youtube.com/playlist?list=PL5nePqlNfqM-D0i7dXmtHybzEoMCGz7nr';

const entryBenefits = [
  {
    icon: Zap,
    label: 'Energia Limpa',
    desc: 'Economia real na sua conta de luz todos os meses.',
    highlight: 'Até 25% de desconto',
  },
  {
    icon: Shield,
    label: 'Seguros',
    desc: 'Proteção completa para você e sua família.',
    highlight: 'Coberturas exclusivas',
  },
  {
    icon: Wifi,
    label: 'Telecom',
    desc: 'Internet e telefonia com os melhores planos.',
    highlight: 'Conexão de alta velocidade',
  },
  {
    icon: Users,
    label: 'Seja Licenciado',
    desc: 'Renda extra indicando os produtos iGreen.',
    highlight: 'Comissões recorrentes',
  },
];

const clubBenefits = [
  {
    icon: Pill,
    label: 'Farmácias',
    desc: 'Descontos em medicamentos e produtos de saúde nas principais redes.',
    pct: 'Até 70% off',
  },
  {
    icon: Shirt,
    label: 'Moda',
    desc: 'Roupas, calçados e acessórios das melhores marcas.',
    pct: 'Até 50% off',
  },
  {
    icon: Utensils,
    label: 'Alimentação',
    desc: 'Restaurantes, hamburguerias, pizzarias e mais.',
    pct: 'Até 40% off',
  },
  {
    icon: Clapperboard,
    label: 'Cinema & Lazer',
    desc: 'Ingressos com desconto para filmes, shows e eventos.',
    pct: 'Até 50% off',
  },
  {
    icon: Plane,
    label: 'Viagens',
    desc: 'Hotéis, passagens e pacotes turísticos.',
    pct: 'Até 35% off',
  },
  {
    icon: ShoppingBag,
    label: 'Varejo',
    desc: 'Eletrodomésticos, eletrônicos, e-commerce e muito mais.',
    pct: 'Até 60% off',
  },
];

function BenefitCard({ item, index }: { item: typeof entryBenefits[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl bg-gradient-to-br from-primary-500/15 to-primary-500/5 border border-background-800/60 ring-1 ring-primary-400/25 p-6 md:p-7 flex flex-col items-center text-center sm:items-start sm:text-left gap-4 transition-all duration-500 hover:-translate-y-1.5 hover:border-primary-500/40 cursor-default ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Glow spot */}
      <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-primary-500 opacity-10 blur-2xl" />

      <div className="w-16 h-16 rounded-2xl bg-background-900/80 ring-1 ring-primary-400/30 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
        <item.icon className="w-10 h-10 text-primary-400" />
      </div>

      <div>
        <h3 className="text-lg font-extrabold text-white leading-tight">{item.label}</h3>
        <p className="mt-1.5 text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
      </div>

      <div className="inline-flex self-center sm:self-start items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary-400/20 text-primary-300">
        <CheckCircle className="w-3.5 h-3.5" />
        {item.highlight}
      </div>
    </div>
  );
}

function ClubCard({ item, index }: { item: typeof clubBenefits[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group flex flex-col items-center text-center sm:items-stretch sm:text-left gap-4 p-6 rounded-2xl bg-background-900/70 border border-background-800/60 hover:border-primary-500/40 hover:bg-background-900 transition-all duration-400 hover:-translate-y-1 cursor-default ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="w-14 h-14 rounded-xl bg-primary-500/10 ring-1 ring-primary-400/20 flex items-center justify-center text-primary-400 group-hover:bg-primary-500/20 transition-colors duration-300 shrink-0">
          <item.icon className="w-7 h-7" />
        </div>
        <span className="px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-300 text-xs font-bold ring-1 ring-primary-400/20 whitespace-nowrap">
          {item.pct}
        </span>
      </div>

      <div>
        <h4 className="font-extrabold text-white text-base">{item.label}</h4>
        <p className="mt-1 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
      </div>
    </div>
  );
}

export default function ClubSection() {
  return (
    <section id="club" className="relative overflow-hidden bg-background-950 px-5 py-20 sm:px-8 md:py-24 lg:px-16 xl:px-24">
      {/* Background glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-radial-green rounded-full pointer-events-none opacity-25" />
      <div className="absolute bottom-0 -right-32 w-[600px] h-[500px] bg-radial-green rounded-full pointer-events-none opacity-15" />
      <div className="absolute inset-0 dot-grid pointer-events-none opacity-[0.04]" />

      <div className="relative z-10 flex min-h-[calc(200svh-11rem)] w-full flex-col justify-center">
        {/* ── Header ── */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-semibold mb-6">
              <Gift className="w-4 h-4" />
              iGreen Club
            </span>

            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter leading-[1.1]">
              Um benefício para todos
            </h2>

            <div className="mt-5 flex justify-center">
              <span className="block w-16 h-1 rounded-full bg-primary-400" />
            </div>

            <p className="mt-6 text-lg md:text-xl font-semibold text-primary-400 leading-relaxed">
              Contrate qualquer serviço iGreen ou torne-se um licenciado e ganhe acesso gratuito ao iGreen Club.
            </p>

            <p className="mt-3 text-base md:text-lg text-foreground-400 leading-relaxed">
              Descontos em mais de 600 mil produtos e serviços em todo o Brasil.
            </p>
          </div>
        </ScrollReveal>

        {/* ── Step 1: Como entrar ── */}
        <div className="mt-16 md:mt-20">
          <ScrollReveal>
            <div className="mb-8 flex flex-col items-center gap-3 text-center md:flex-row md:gap-4 md:text-left">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 ring-2 ring-primary-400/40 flex items-center justify-center text-primary-400 font-extrabold text-sm shrink-0">
                1
              </div>
              <h3 className="text-xl md:text-2xl font-extrabold text-white">
                Contrate um serviço ou torne-se licenciado
              </h3>
              <div className="hidden h-px flex-1 bg-gradient-to-r from-primary-400/30 to-transparent md:block" />
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {entryBenefits.map((item, i) => (
              <BenefitCard key={item.label} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* ── Arrow divider ── */}
        <ScrollReveal>
          <div className="flex flex-col items-center gap-2 my-10 md:my-14">
            <div className="w-px h-8 bg-gradient-to-b from-primary-400/60 to-transparent" />
            <div className="w-16 h-16 rounded-full bg-primary-500/10 ring-2 ring-primary-400/40 flex items-center justify-center shadow-[0_0_24px_4px] shadow-primary-500/20">
              <Gift className="w-8 h-8 text-primary-400" />
            </div>
            <div className="px-5 py-2 rounded-full bg-primary-500/10 border border-primary-400/20 text-primary-400 text-sm font-bold">
              Ganhe acesso gratuito ao
            </div>
            <div className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary-600 to-primary-500 text-white text-lg font-extrabold shadow-[0_0_20px_2px] shadow-primary-500/30">
              iGreen Club
            </div>
            <div className="w-px h-8 bg-gradient-to-b from-transparent to-primary-400/60" />
          </div>
        </ScrollReveal>

        {/* ── Step 2: Categorias de desconto ── */}
        <div>
          <ScrollReveal>
            <div className="mb-8 flex flex-col items-center gap-3 text-center md:flex-row md:gap-4 md:text-left">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 ring-2 ring-primary-400/40 flex items-center justify-center text-primary-400 font-extrabold text-sm shrink-0">
                2
              </div>
              <h3 className="text-xl md:text-2xl font-extrabold text-white">
                Aproveite descontos em +600 mil produtos
              </h3>
              <div className="hidden h-px flex-1 bg-gradient-to-r from-primary-400/30 to-transparent md:block" />
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {clubBenefits.map((item, i) => (
              <ClubCard key={item.label} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <ScrollReveal>
          <div className="mt-14 md:mt-18 rounded-2xl bg-gradient-to-br from-primary-500/15 via-primary-500/5 to-background-900/60 border border-primary-400/20 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white">
                Pronto para fazer parte?
              </h3>
              <p className="mt-2 text-foreground-400 text-base md:text-lg">
                Fale com um especialista e comece a economizar hoje mesmo.
              </p>
            </div>
            <a
              href={WHATSAPP_CLUB}
              target="_blank"
              rel="noreferrer"
              className="btn-primary w-full md:w-auto text-base px-8 py-4 group shrink-0 whitespace-nowrap"
            >
              Conhecer o iGreen Club
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </ScrollReveal>

        {/* ── Testimonial ── */}
        <div className="mt-10 grid grid-cols-1 items-stretch gap-6 md:mt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_13rem]">
          <ScrollReveal>
            <div className="flex h-full flex-col items-center justify-center text-center md:items-start md:text-left">
              <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-white tracking-tighter">
                Confira alguns <span className="text-gradient">depoimentos</span>
              </h3>
              <p className="mt-3 text-foreground-400 leading-relaxed">
                Quem já faz parte do iGreen Club aproveita descontos e vantagens todos os dias.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="rounded-2xl bg-background-900/60 p-6 text-center backdrop-blur-sm border border-background-800/50 md:p-8 md:text-left">
              <Quote className="mx-auto mb-4 h-8 w-8 text-primary-400 md:mx-0" />
              <p className="text-base md:text-lg text-foreground-200 leading-relaxed">
                &ldquo;Com o iGreen Club eu encontro descontos perto de casa e ainda acumulo cashback nas minhas contas. Virou parte da minha rotina.&rdquo;
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 md:justify-start">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold">
                  C
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Cliente iGreen</p>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="w-4 h-4 text-primary-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={220} className="h-full">
            <a
              id="club-playlist"
              href={CLUB_PLAYLIST_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Abrir a playlist de depoimentos do iGreen Club no YouTube"
              className="group flex h-full flex-col items-center justify-center rounded-2xl border border-primary-400/25 bg-primary-500/10 p-5 text-center transition-colors hover:bg-primary-500/15"
            >
              <span className="mb-3 inline-flex items-center gap-2 text-sm font-bold text-primary-300">
                <Youtube className="h-5 w-5" />
                Playlist no YouTube
              </span>
              <span className="rounded-xl bg-white p-2 shadow-lg">
                <img
                  src={`${__BASE_PATH__}assets/qr-igreen-club-depoimentos.png`}
                  alt="QR Code para a playlist de depoimentos do iGreen Club"
                  className="h-auto w-36 max-w-full object-contain"
                  width="640"
                  height="640"
                  loading="lazy"
                />
              </span>
              <span className="mt-3 text-xs leading-relaxed text-foreground-400 group-hover:text-foreground-200">
                Escaneie ou toque para assistir
              </span>
            </a>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
